import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMe, logout as logoutApi } from '../api/auth';
import {
  getInvitations,
  createInvitation,
  deleteInvitation,
  getUsers,
  toggleUserActive,
  deleteUser
} from '../api/admin';
import Sidebar from '../components/Sidebar';

function Admin() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('invitations');
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const userData = await getMe();
        if (userData.role !== 'admin') {
          alert('관리자 권한이 필요합니다.');
          navigate('/dashboard');
          return;
        }
        setUser(userData);
        await loadData();
      } catch (err) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [navigate]);

  const handleLogout = () => {
    logoutApi();
    navigate('/login');
  };

  const loadData = async () => {
    try {
      const [invRes, userRes] = await Promise.all([
        getInvitations(),
        getUsers()
      ]);
      setInvitations(invRes.invitations);
      setUsers(userRes.users);
    } catch (err) {
      console.error('데이터 로드 실패:', err);
    }
  };

  const handleCreateInvitation = async () => {
    setCreating(true);
    try {
      await createInvitation(7);
      await loadData();
    } catch (err) {
      alert('초대코드 생성에 실패했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteInvitation = async (code) => {
    if (!window.confirm('이 초대코드를 삭제하시겠습니까?')) return;
    try {
      await deleteInvitation(code);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const handleToggleUser = async (userId) => {
    try {
      await toggleUserActive(userId);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || '상태 변경에 실패했습니다.');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`"${userName}" 사용자를 삭제하시겠습니까?`)) return;
    try {
      await deleteUser(userId);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || '삭제에 실패했습니다.');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (dateStr) => {
    return new Date(dateStr) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar user={user} onLogout={handleLogout} />

      {/* 메인 콘텐츠 */}
      <main className="p-4 md:p-8 pt-20 md:pt-24 w-full max-w-6xl mx-auto">
        <header className="flex justify-between items-center mb-3 md:mb-4">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-base md:text-2xl font-bold text-gray-800">관리자</h1>
              <p className="text-xs text-gray-500">사용자 및 초대코드 관리</p>
            </div>
            <div className="flex space-x-1 ml-2">
              <button
                onClick={() => setActiveTab('invitations')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'invitations'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                초대코드
              </button>
              <button
                onClick={() => setActiveTab('users')}
                className={`px-2 py-1 text-xs font-medium rounded transition ${
                  activeTab === 'users'
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
              >
                사용자
              </button>
            </div>
          </div>
        </header>

        <div className="space-y-2">
          {/* 초대코드 관리 */}
          {activeTab === 'invitations' && (
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-3">
                  <h3 className="text-sm text-gray-500 font-medium">초대코드 목록</h3>
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                    {invitations.length}개
                  </span>
                </div>
                <button
                  onClick={handleCreateInvitation}
                  disabled={creating}
                  className="bg-primary-500 text-white px-3 py-1 text-xs rounded-lg font-medium hover:bg-primary-600 transition disabled:opacity-50"
                >
                  {creating ? '생성 중...' : '+ 초대코드 생성'}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">코드</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">상태</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">만료일</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">생성일</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-2">
                          <span className="font-mono text-xs font-semibold text-gray-800">{inv.code}</span>
                        </td>
                        <td className="py-2 px-2">
                          {inv.used_by ? (
                            <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">사용됨</span>
                          ) : isExpired(inv.expires_at) ? (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">만료됨</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">사용가능</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-600">{formatDate(inv.expires_at)}</td>
                        <td className="py-2 px-2 text-xs text-gray-600">{formatDate(inv.created_at)}</td>
                        <td className="py-2 px-2 text-right">
                          {!inv.used_by && (
                            <button
                              onClick={() => handleDeleteInvitation(inv.code)}
                              className="text-red-500 hover:text-red-700 text-xs"
                            >
                              삭제
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {invitations.length === 0 && (
                      <tr>
                        <td colSpan="5" className="py-6 text-center text-xs text-gray-400">
                          초대코드가 없습니다
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 사용자 관리 */}
          {activeTab === 'users' && (
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-3">
                  <h3 className="text-sm text-gray-500 font-medium">사용자 목록</h3>
                  <span className="text-xs bg-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                    {users.length}명
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">이름</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">이메일</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">권한</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">상태</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-gray-500">가입일</th>
                      <th className="text-right py-2 px-2 text-xs font-medium text-gray-500">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50">
                        <td className="py-2 px-2 text-xs font-medium text-gray-800">{u.name}</td>
                        <td className="py-2 px-2 text-xs text-gray-600">{u.email}</td>
                        <td className="py-2 px-2">
                          {u.role === 'admin' ? (
                            <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 text-xs rounded-full">관리자</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full">사용자</span>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          {u.is_active ? (
                            <span className="px-1.5 py-0.5 bg-green-100 text-green-600 text-xs rounded-full">활성</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">비활성</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-600">{formatDate(u.created_at)}</td>
                        <td className="py-2 px-2 text-right">
                          {u.role !== 'admin' && (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => handleToggleUser(u.id)}
                                className={`text-xs ${u.is_active ? 'text-orange-500 hover:text-orange-700' : 'text-green-500 hover:text-green-700'}`}
                              >
                                {u.is_active ? '비활성화' : '활성화'}
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                className="text-red-500 hover:text-red-700 text-xs"
                              >
                                삭제
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default Admin;
